import { useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Dialog,
  DropdownMenu,
  Flex,
  Grid,
  IconButton,
  RadioGroup,
  Select,
  Separator,
  Switch,
  Text,
  TextArea,
  TextField,
  Tooltip,
} from "@radix-ui/themes";
import {
  ArrowLeft,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  MapPin,
  MoreVertical,
  Plus,
  Sparkles,
  Upload,
  X,
} from "lucide-react";
import { ToastAlert } from "components";
import { useLocationContext, useProfileContext } from "context";
import {
  useCancelGooglePost,
  useGenerateGooglePostCaption,
  useGetGooglePosts,
  useUpdateGooglePostQueueItem,
  useUpdateGooglePostSchedule,
  useUploadGooglePosts,
} from "hooks";
import { useToast } from "../../context/ToastContext";
import "../../components/calendar/ReviewsPostsCalendar.css";
import { useSearchParams } from "react-router-dom";

const DEFAULT_PHOTO_CATEGORIES = [
  { value: "FOOD_AND_DRINK", label: "Food" },
  { value: "INTERIOR", label: "Atmosphere" },
  { value: "TEAMS", label: "Team" },
  { value: "EXTERIOR", label: "Exterior" },
  { value: "PRODUCT", label: "Product" },
];
const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const panelStyle = {
  border: "1px solid var(--gray-a4)",
  boxShadow: "0 5px 18px var(--black-a2)",
};

function formatSchedule(date) {
  if (!date || Number.isNaN(date.getTime())) return "Waiting for a schedule";
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function formatCalendarTime(date) {
  if (!date || Number.isNaN(date.getTime())) return "Not scheduled";
  return new Intl.DateTimeFormat("en-GB", {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function calendarCells(monthDate) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const leadingEmptyCells = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [
    ...Array.from({ length: leadingEmptyCells }, () => null),
    ...Array.from({ length: daysInMonth }, (_, index) => index + 1),
  ];
  const trailingEmptyCells = (7 - (cells.length % 7)) % 7;
  return [
    ...cells,
    ...Array.from({ length: trailingEmptyCells }, () => null),
  ];
}

function categoryColor(category) {
  if (category === "FOOD_AND_DRINK") return "orange";
  if (category === "TEAMS") return "green";
  return "violet";
}

function formatTimeValue(value = "09:00") {
  const [rawHour, minutes] = value.slice(0, 5).split(":");
  const hour = Number(rawHour);
  return `${hour % 12 || 12}:${minutes} ${hour >= 12 ? "PM" : "AM"}`;
}

function parseTimeValue(value) {
  const match = value.match(/^(\d{1,2}):(\d{2})\s(AM|PM)$/);
  if (!match) return "09:00";
  let hour = Number(match[1]) % 12;
  if (match[3] === "PM") hour += 12;
  return `${String(hour).padStart(2, "0")}:${match[2]}`;
}

function scheduleCadence(schedule) {
  const unit = schedule.frequency.toLowerCase();
  const interval = schedule.interval === "1"
    ? unit
    : `${schedule.interval} ${unit}s`;
  const selectedDay = schedule.frequency === "Day" ? "" : ` on ${schedule.day}`;
  return `every ${interval}${selectedDay} at ${schedule.time}`;
}

function mapQueueItem(item) {
  return {
    ...item,
    name: item.original_filename,
    title: item.original_filename.replace(/\.[^.]+$/, "").replace(/[-_]/g, " "),
    previewUrl: item.image_url,
    categoryLabel: item.category_label,
    postTo: item.destination,
    callToActionType: item.call_to_action_type || "",
    callToActionUrl: item.call_to_action_url || "",
    scheduledAt: item.scheduled_for ? new Date(item.scheduled_for) : null,
    publishedAt: item.published_at ? new Date(item.published_at) : null,
  };
}

function isPublishingItem(item, currentTime = Date.now()) {
  return item.status === "processing" || (
    ["queued", "scheduled"].includes(item.status)
    && item.scheduledAt
    && item.scheduledAt.getTime() <= currentTime
  );
}

function PhotoPlaceholder({ photo, height = 130 }) {
  return (
    <Flex
      align="center"
      justify="center"
      style={{
        position: "relative",
        height,
        minWidth: 0,
        overflow: "hidden",
        background: "var(--gray-a3)",
        borderRadius: "var(--radius-3)",
      }}
    >
      {photo?.previewUrl ? (
        <img
          src={photo.previewUrl}
          alt={photo.name}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <ImageIcon size={34} color="var(--gray-7)" />
      )}
    </Flex>
  );
}

function CalendarItemTooltip({ item, children }) {
  return (
    <Tooltip
      side="top"
      sideOffset={8}
      className="reviews-posts-calendar-tooltip"
      content={(
        <span className="reviews-posts-calendar-tooltip-content">
          <span className="reviews-posts-calendar-tooltip-title">{item.title}</span>
          <span className="reviews-posts-calendar-tooltip-meta">
            {formatCalendarTime(item.scheduledAt || item.publishedAt)} · {item.postTo === "post" ? "Post" : "Photo"}
          </span>
        </span>
      )}
    >
      {children}
    </Tooltip>
  );
}

function UploadSteps({ step }) {
  const labels = ["Upload", "Set Details", "Review & Add"];
  return (
    <Flex align="start" justify="center" gap="3" wrap="wrap">
      {labels.map((label, index) => {
        const number = index + 1;
        const complete = step > number;
        const active = step === number;
        return (
          <Flex key={label} align="center" gap="3">
            <Flex direction="column" align="center" gap="1">
              <Flex
                align="center"
                justify="center"
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  background: complete ? "var(--green-9)" : active ? "var(--blue-9)" : "var(--gray-a4)",
                  color: complete || active ? "white" : "var(--gray-9)",
                }}
              >
                {complete ? <Check size={17} /> : <Text weight="bold">{number}</Text>}
              </Flex>
              <Text size="1" weight="medium" color={complete ? "green" : active ? "blue" : "gray"}>
                {label}
              </Text>
            </Flex>
            {index < labels.length - 1 && (
              <div style={{ width: 54, height: 2, marginBottom: 20, background: complete ? "var(--green-9)" : "var(--gray-a5)" }} />
            )}
          </Flex>
        );
      })}
    </Flex>
  );
}

function ScheduleDialog({ open, onOpenChange, locationName, settings, onSave, isSaving }) {
  const [draft, setDraft] = useState(settings);

  useEffect(() => {
    if (open) setDraft(settings);
  }, [open, settings]);

  const updateDraft = (field, value) => setDraft((current) => ({ ...current, [field]: value }));

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="560px">
        <Flex justify="between" align="start" gap="3">
          <Flex direction="column" gap="1">
            <Dialog.Title>Schedule Settings</Dialog.Title>
            <Dialog.Description>
              Configure how often photos are posted for {locationName}.
            </Dialog.Description>
          </Flex>
          <Dialog.Close>
            <IconButton variant="ghost" color="gray" aria-label="Close schedule settings">
              <X size={18} />
            </IconButton>
          </Dialog.Close>
        </Flex>

        <Flex direction="column" gap="4" mt="5">
          <Flex justify="between" align="center" gap="3">
            <Flex direction="column" gap="1">
              <Text weight="bold">Auto-schedule</Text>
              <Text size="2" color="gray">Automatically post photos from your queue on a schedule.</Text>
            </Flex>
            <Switch checked={draft.enabled} onCheckedChange={(value) => updateDraft("enabled", value)} />
          </Flex>
          <Separator size="4" />

          <Flex direction="column" gap="2">
            <Text weight="bold">Post every</Text>
            <Flex gap="2">
              <Select.Root value={draft.interval} onValueChange={(value) => updateDraft("interval", value)}>
                <Select.Trigger style={{ width: 88 }} />
                <Select.Content>
                  {["1", "2", "3", "4"].map((value) => <Select.Item key={value} value={value}>{value}</Select.Item>)}
                </Select.Content>
              </Select.Root>
              <Select.Root value={draft.frequency} onValueChange={(value) => updateDraft("frequency", value)}>
                <Select.Trigger style={{ flex: 1 }} />
                <Select.Content>
                  <Select.Item value="Day">Day</Select.Item>
                  <Select.Item value="Week">Week</Select.Item>
                  <Select.Item value="Month">Month</Select.Item>
                </Select.Content>
              </Select.Root>
            </Flex>
            <Text size="1" color="gray" style={{ fontStyle: "italic" }}>
              One photo will be posted {scheduleCadence(draft)}.
            </Text>
          </Flex>
          {draft.frequency !== "Day" && (
            <>
              <Separator size="4" />
              <Flex direction="column" gap="2">
                <Text weight="bold">Day of week</Text>
                <Flex gap="2" wrap="wrap">
                  {WEEK_DAYS.map((day) => (
                    <Button key={day} size="2" variant={draft.day === day ? "solid" : "outline"} onClick={() => updateDraft("day", day)}>
                      {day}
                    </Button>
                  ))}
                </Flex>
              </Flex>
            </>
          )}
          <Separator size="4" />

          <Flex direction="column" gap="2">
            <Text weight="bold">Posting time</Text>
            <TextField.Root
              type="time"
              step="60"
              value={parseTimeValue(draft.time)}
              onChange={(event) => {
                if (event.target.value) {
                  updateDraft("time", formatTimeValue(event.target.value));
                }
              }}
            />
            <Text size="1" color="gray" style={{ fontStyle: "italic" }}>
              Uses the location's timezone. For testing, choose a time a few minutes ahead.
            </Text>
          </Flex>
          <Separator size="4" />

          <Flex direction="column" gap="2">
            <Text weight="bold">Default post to</Text>
            <RadioGroup.Root value={draft.postTo} onValueChange={(value) => updateDraft("postTo", value)}>
              <RadioGroup.Item value="photo">Google Photos section</RadioGroup.Item>
              <RadioGroup.Item value="post">Google Posts (What's New)</RadioGroup.Item>
            </RadioGroup.Root>
          </Flex>

          <Card variant="surface">
            <Text size="2">
              Posts {scheduleCadence(draft)} to Google {draft.postTo === "photo" ? "Photos" : "Posts"} · {locationName}
            </Text>
          </Card>

          <Flex justify="end" gap="2">
            <Dialog.Close><Button variant="soft" color="gray" disabled={isSaving}>Cancel</Button></Dialog.Close>
            <Button disabled={isSaving} onClick={() => onSave(draft)}>
              {isSaving ? "Saving..." : "Save Settings"}
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function PublishedItemDialog({ item, onClose }) {
  return (
    <Dialog.Root open={Boolean(item)} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Content maxWidth="680px">
        {item && (
          <Flex direction="column" gap="4">
            <Flex justify="between" align="start" gap="3">
              <Flex direction="column" gap="1">
                <Dialog.Title>{item.title}</Dialog.Title>
                <Dialog.Description>Published Google content details</Dialog.Description>
              </Flex>
              <Dialog.Close>
                <IconButton variant="ghost" color="gray" aria-label="Close published photo details">
                  <X size={18} />
                </IconButton>
              </Dialog.Close>
            </Flex>

            <PhotoPlaceholder photo={item} height={320} />

            <Grid columns={{ initial: "1", sm: "1fr 1fr" }} gap="3">
              <Flex direction="column" gap="1">
                <Text size="1" weight="bold" color="gray">CATEGORY</Text>
                <Badge color={categoryColor(item.category)} variant="soft" style={{ alignSelf: "flex-start" }}>
                  {item.categoryLabel}
                </Badge>
              </Flex>
              <Flex direction="column" gap="1">
                <Text size="1" weight="bold" color="gray">PUBLISHED TO</Text>
                <Text>{item.postTo === "post" ? "Google Post" : "Google Photos"}</Text>
              </Flex>
              <Flex direction="column" gap="1">
                <Text size="1" weight="bold" color="gray">SCHEDULED FOR</Text>
                <Text>{formatSchedule(item.scheduledAt)}</Text>
              </Flex>
              <Flex direction="column" gap="1">
                <Text size="1" weight="bold" color="gray">PUBLISHED AT</Text>
                <Text>{formatSchedule(item.publishedAt || item.scheduledAt)}</Text>
              </Flex>
            </Grid>

            <Separator size="4" />
            <Flex direction="column" gap="2">
              <Text size="1" weight="bold" color="gray">CAPTION</Text>
              <Text style={{ whiteSpace: "pre-wrap" }}>{item.caption || "No caption added."}</Text>
            </Flex>

            <Flex justify="between" align="center" gap="3">
              <Badge color="green" variant="soft"><Check size={13} /> Published</Badge>
              <Dialog.Close><Button variant="soft" color="gray">Close</Button></Dialog.Close>
            </Flex>
          </Flex>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}

const GOOGLE_POST_BUTTON_TYPES = [
  ["", "No button"],
  ["BOOK", "Book"],
  ["ORDER", "Order online"],
  ["SHOP", "Buy"],
  ["LEARN_MORE", "Learn more"],
  ["SIGN_UP", "Sign up"],
  ["CALL", "Call"],
];

function googlePostLinkError(draft) {
  if (draft.postTo !== "post" && draft.destination !== "post") return "";
  if (!draft.callToActionType || draft.callToActionType === "CALL") return "";
  const value = (draft.callToActionUrl || "").trim();
  if (!value) return "Enter a link for the selected button.";
  if (!/^https?:\/\//i.test(value)) {
    return "Enter a valid link beginning with http:// or https://.";
  }
  try {
    const url = new URL(value);
    if (!["http:", "https:"].includes(url.protocol) || !url.hostname) {
      return "Enter a valid link beginning with http:// or https://.";
    }
  } catch {
    return "Enter a valid link beginning with http:// or https://.";
  }
  return "";
}

function GooglePostButtonFields({ draft, setDraft, disabled = false }) {
  const linkError = googlePostLinkError(draft);
  return (
    <Flex direction="column" gap="2">
      <Text size="1" weight="bold" color="gray">ADD MORE DETAILS</Text>
      <Select.Root
        value={draft.callToActionType || "none"}
        disabled={disabled}
        onValueChange={(value) =>
          setDraft((current) => ({
            ...current,
            callToActionType: value === "none" ? "" : value,
            callToActionUrl: value === "CALL" || value === "none" ? "" : current.callToActionUrl,
          }))
        }
      >
        <Select.Trigger placeholder="Add a button (optional)" />
        <Select.Content>
          {GOOGLE_POST_BUTTON_TYPES.map(([value, label]) => (
            <Select.Item key={value || "none"} value={value || "none"}>{label}</Select.Item>
          ))}
        </Select.Content>
      </Select.Root>
      {draft.callToActionType && draft.callToActionType !== "CALL" && (
        <TextField.Root
          type="url"
          color={linkError ? "red" : undefined}
          disabled={disabled}
          placeholder="https://example.com"
          value={draft.callToActionUrl || ""}
          onChange={(event) =>
            setDraft((current) => ({ ...current, callToActionUrl: event.target.value }))
          }
        />
      )}
      {linkError && <Text size="1" color="red">{linkError}</Text>}
      {draft.callToActionType === "CALL" && (
        <Text size="1" color="gray">Google uses the phone number on this Business Profile.</Text>
      )}
    </Flex>
  );
}

function QueueItemEditor({ item, categories, onCancel, onSave, isSaving, isLocked }) {
  const [draft, setDraft] = useState({
    caption: item.caption || "",
    category: item.category,
    destination: item.postTo,
    callToActionType: item.callToActionType || "",
    callToActionUrl: item.callToActionUrl || "",
  });

  useEffect(() => {
    setDraft({
      caption: item.caption || "",
      category: item.category,
      destination: item.postTo,
      callToActionType: item.callToActionType || "",
      callToActionUrl: item.callToActionUrl || "",
    });
  }, [item]);

  const linkError = googlePostLinkError(draft);

  return (
    <Flex direction="column" gap="4" pt="4" mt="3" style={{ borderTop: "1px solid var(--gray-a5)" }}>
      <Flex direction="column" gap="1">
        <Text size="1" weight="bold" color="gray">CATEGORY</Text>
        <Select.Root
          value={draft.category}
          disabled={isLocked}
          onValueChange={(category) => setDraft((current) => ({ ...current, category }))}
        >
          <Select.Trigger />
          <Select.Content>
            {categories.map((category) => (
              <Select.Item key={category.value} value={category.value}>
                {category.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      </Flex>
      <Flex direction="column" gap="2">
        <Text size="1" weight="bold" color="gray">POST TO</Text>
        <Flex gap="2" wrap="wrap">
          <Button
            disabled={isLocked}
            variant={draft.destination === "photo" ? "solid" : "outline"}
            onClick={() => setDraft((current) => ({ ...current, destination: "photo", callToActionType: "", callToActionUrl: "" }))}
          >
            Google Photo
          </Button>
          <Button
            disabled={isLocked}
            variant={draft.destination === "post" ? "solid" : "outline"}
            onClick={() => setDraft((current) => ({ ...current, destination: "post" }))}
          >
            Google Post
          </Button>
        </Flex>
      </Flex>
      <Flex direction="column" gap="1">
        <Text size="1" weight="bold" color="gray">CAPTION</Text>
        <TextArea
          disabled={isLocked}
          rows={5}
          maxLength={300}
          placeholder="Write a caption..."
          value={draft.caption}
          onChange={(event) => setDraft((current) => ({ ...current, caption: event.target.value }))}
        />
        <Text size="1" color="gray" align="right">{draft.caption.length} / 300 characters</Text>
      </Flex>
      {draft.destination === "post" && (
        <GooglePostButtonFields draft={draft} setDraft={setDraft} disabled={isLocked} />
      )}
      <Flex direction="column" gap="1">
        <Text size="1" weight="bold" color="gray">SCHEDULED DATE</Text>
        <Text>{formatSchedule(item.scheduledAt)}</Text>
      </Flex>
      <Flex justify="between" gap="2" wrap="wrap">
        <Button color="gray" variant="soft" disabled={isSaving} onClick={onCancel}>Cancel</Button>
        <Button disabled={isSaving || isLocked || Boolean(linkError)} onClick={() => onSave(item, draft)}>
          {isLocked ? "Publishing" : isSaving ? "Saving..." : "Save changes"}
        </Button>
      </Flex>
    </Flex>
  );
}

function PostsDashboard({ locationName, locationControl, queue, published, monthDate, setMonthDate, schedule, categories, editingItem, isReady, onUpload, onSchedule, onEdit, onCancelEdit, onSaveEdit, onPostLater, onPostNow, onCancel, isLoading, isUpdating }) {
  const monthLabel = new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric" }).format(monthDate);
  const cells = calendarCells(monthDate);
  const calendarItems = [...queue, ...published];
  const [removingItemId, setRemovingItemId] = useState(null);
  const [selectedPublishedItem, setSelectedPublishedItem] = useState(null);
  const [currentTime, setCurrentTime] = useState(Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setCurrentTime(Date.now()), 10000);
    return () => window.clearInterval(timer);
  }, []);

  const confirmRemove = async (item) => {
    const removed = await onCancel(item);
    if (removed) setRemovingItemId(null);
  };

  return (
    <Flex direction="column" gap="4">
      <Flex justify="between" align="center" gap="3" wrap="wrap">
        {locationControl || <Text weight="bold">{locationName}</Text>}
        <Flex gap="2" wrap="wrap">
          <Button disabled={!isReady} onClick={onUpload}><Plus size={16} /> Upload Photos</Button>
          <Button disabled={!isReady} variant="outline" onClick={onSchedule}>Schedule Settings</Button>
        </Flex>
      </Flex>

      <Grid columns={{ initial: "1", lg: "minmax(300px, 0.8fr) minmax(540px, 1.4fr)" }} gap="4" align="start">
        <Card size="3" style={panelStyle}>
          <Flex direction="column" gap="3">
            <Flex align="center" gap="2">
              <Text size="4" weight="bold">Photo Queue</Text>
              <Badge color="violet" radius="full">
                {queue.length} {schedule.enabled ? "scheduled" : "queued"}
              </Badge>
            </Flex>
            <Text size="2" color="gray">
              {schedule.enabled
                ? "Photos will post in this order."
                : "Auto-scheduling is off. Use Post now to publish an item."}
            </Text>
            <Flex
              direction="column"
              gap="2"
              style={{
                maxHeight: 620,
                overflowY: "auto",
                paddingRight: 4,
              }}
            >
              {isLoading ? (
                <Flex justify="center" py="6"><Text color="gray">Loading queue...</Text></Flex>
              ) : queue.length === 0 ? (
                <Flex direction="column" align="center" gap="2" py="6">
                  <ImageIcon size={30} color="var(--gray-7)" />
                  <Text color="gray">Your photo queue is empty.</Text>
                </Flex>
              ) : queue.map((item) => (
                <Card
                  key={item.id}
                  variant="surface"
                  style={{
                    flexShrink: 0,
                    ...(editingItem?.id === item.id
                      ? { border: "1px solid var(--blue-9)" }
                      : {}),
                  }}
                >
                  <Flex align="center" gap="3">
                    <div style={{ width: 54, flexShrink: 0 }}><PhotoPlaceholder photo={item} height={54} /></div>
                    <Flex direction="column" gap="1" style={{ flex: 1, minWidth: 0 }}>
                      <Text weight="bold" truncate>{item.title}</Text>
                      <Text size="2" color="gray">{formatSchedule(item.scheduledAt)}</Text>
                      <Badge color={categoryColor(item.category)} variant="soft" style={{ alignSelf: "flex-start" }}>{item.categoryLabel}</Badge>
                      {item.error_message && <Text size="1" color="red">{item.error_message}</Text>}
                    </Flex>
                    <Flex direction="column" align="end" gap="2">
                      <Badge color={item.postTo === "post" ? "green" : "violet"} variant="soft">
                        {item.postTo === "post" ? "Post" : "Photo"}
                      </Badge>
                      <Badge
                        color={item.status === "failed" ? "red" : "gray"}
                        variant="soft"
                      >
                        {isPublishingItem(item, currentTime) ? "publishing" : item.status}
                      </Badge>
                      <DropdownMenu.Root>
                        <DropdownMenu.Trigger>
                          <IconButton
                            variant="ghost"
                            color="gray"
                            disabled={
                              isUpdating
                              || removingItemId === item.id
                              || isPublishingItem(item, currentTime)
                            }
                            aria-label={`Actions for ${item.title}`}
                          >
                            <MoreVertical size={17} />
                          </IconButton>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Content align="end">
                          <DropdownMenu.Item onSelect={() => onEdit(item)}>Edit</DropdownMenu.Item>
                          {schedule.enabled ? (
                            <DropdownMenu.Item color="orange" onSelect={() => onPostLater(item)}>
                              Post Later
                            </DropdownMenu.Item>
                          ) : (
                            <DropdownMenu.Item color="green" onSelect={() => onPostNow(item)}>
                              Post now
                            </DropdownMenu.Item>
                          )}
                          <DropdownMenu.Separator />
                          <DropdownMenu.Item color="red" onSelect={() => setRemovingItemId(item.id)}>Remove from Queue</DropdownMenu.Item>
                        </DropdownMenu.Content>
                      </DropdownMenu.Root>
                    </Flex>
                  </Flex>
                  {editingItem?.id === item.id && (
                    <QueueItemEditor
                      item={item}
                      categories={categories}
                      onCancel={onCancelEdit}
                      onSave={onSaveEdit}
                      isSaving={isUpdating}
                      isLocked={isPublishingItem(item, currentTime)}
                    />
                  )}
                  {removingItemId === item.id && (
                    <Flex
                      align="center"
                      justify="between"
                      gap="3"
                      wrap="wrap"
                      mt="3"
                      p="3"
                      style={{
                        background: "var(--red-a3)",
                        borderTop: "1px solid var(--red-a5)",
                        marginLeft: "calc(-1 * var(--space-3))",
                        marginRight: "calc(-1 * var(--space-3))",
                        marginBottom: "calc(-1 * var(--space-3))",
                        borderRadius: "0 0 var(--radius-3) var(--radius-3)",
                      }}
                    >
                      <Text color="red" size="3">Remove this photo from queue?</Text>
                      <Flex gap="2">
                        <Button
                          color="red"
                          variant="outline"
                          disabled={isUpdating}
                          onClick={() => setRemovingItemId(null)}
                        >
                          Keep
                        </Button>
                        <Button
                          color="red"
                          disabled={isUpdating || isPublishingItem(item, currentTime)}
                          onClick={() => confirmRemove(item)}
                        >
                          {isUpdating ? "Removing..." : "Remove"}
                        </Button>
                      </Flex>
                    </Flex>
                  )}
                </Card>
              ))}
            </Flex>
            <Text size="1" color="gray" style={{ fontStyle: "italic" }}>
              {schedule.enabled
                ? `Posts ${scheduleCadence(schedule)} · ${locationName}`
                : `Auto-scheduling is off · ${locationName}`}
            </Text>
          </Flex>
        </Card>

        <Card size="3" style={{ ...panelStyle, overflowX: "auto" }}>
          <Flex direction="column" gap="3" style={{ minWidth: 650 }}>
            <Flex justify="between" align="center">
              <Text size="4" weight="bold">{monthLabel}</Text>
              <Flex gap="2">
                <Button variant="soft" color="gray" onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1))}>
                  <ChevronLeft size={16} /> Prev
                </Button>
                <Button variant="soft" color="gray" onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1))}>
                  Next <ChevronRight size={16} />
                </Button>
              </Flex>
            </Flex>
            <Grid
              columns="repeat(7, minmax(90px, 1fr))"
              gap="0"
              style={{ width: "100%", maxWidth: 1190, alignSelf: "center" }}
            >
              {WEEK_DAYS.map((day) => (
                <Text key={day} size="1" weight="bold" color="gray" align="center" py="2">{day}</Text>
              ))}
              {cells.map((day, index) => {
                const dayPosts = day ? calendarItems.filter((item) => item.scheduledAt && (
                  item.scheduledAt.getFullYear() === monthDate.getFullYear() &&
                  item.scheduledAt.getMonth() === monthDate.getMonth() &&
                  item.scheduledAt.getDate() === day
                )) : [];
                return (
                  <Flex
                    key={`${day || "empty"}-${index}`}
                    direction="column"
                    gap="2"
                    p="2"
                    style={{
                      minWidth: 0,
                      minHeight: 92,
                      overflow: "hidden",
                      borderTop: "1px solid var(--gray-a4)",
                      borderRight: "1px solid var(--gray-a4)",
                      borderLeft:
                        index % 7 === 0
                          ? "1px solid var(--gray-a4)"
                          : undefined,
                      borderBottom:
                        index >= cells.length - 7
                          ? "1px solid var(--gray-a4)"
                          : undefined,
                    }}
                  >
                    {day && <Text size="2" weight="medium">{day}</Text>}
                    {dayPosts.map((post) => post.status === "published" ? (
                      <CalendarItemTooltip key={post.id} item={post}>
                        <button
                          type="button"
                          onClick={() => setSelectedPublishedItem(post)}
                          onFocus={(event) => { event.currentTarget.style.outline = "2px solid var(--blue-8)"; }}
                          onBlur={(event) => { event.currentTarget.style.outline = "none"; }}
                          aria-label={`View published details for ${post.title}`}
                          style={{
                            all: "unset",
                            display: "block",
                            width: "100%",
                            minWidth: 0,
                            cursor: "pointer",
                          }}
                        >
                          <Badge color="green" variant="soft" style={{ display: "flex", width: "100%", maxWidth: "100%", minWidth: 0, overflow: "hidden" }}>
                            <Check size={12} style={{ flexShrink: 0 }} />
                            <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {post.title}
                            </span>
                          </Badge>
                        </button>
                      </CalendarItemTooltip>
                    ) : (
                      <CalendarItemTooltip key={post.id} item={post}>
                        <Badge color={post.postTo === "post" ? "green" : "violet"} variant="soft" style={{ display: "flex", width: "100%", maxWidth: "100%", minWidth: 0, overflow: "hidden" }}>
                          <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {post.title}
                          </span>
                        </Badge>
                      </CalendarItemTooltip>
                    ))}
                  </Flex>
                );
              })}
            </Grid>
          </Flex>
        </Card>
      </Grid>
      <PublishedItemDialog
        item={selectedPublishedItem}
        onClose={() => setSelectedPublishedItem(null)}
      />
    </Flex>
  );
}

function UploadScreen({ locationName, photos, setPhotos, categories, schedule, defaultDestination, onBack, onFinish, onGenerateCaption, onValidationError, generatingPhotoId, isSaving }) {
  const [step, setStep] = useState(1);
  const [removeId, setRemoveId] = useState(null);
  const fileInputRef = useRef(null);
  const categoryLabel = (value) =>
    categories.find((category) => category.value === value)?.label || value;
  const hasInvalidPostButton = photos.some((photo) => Boolean(googlePostLinkError(photo)));

  const addFiles = (files) => {
    const availableFiles = Array.from(files || []).slice(0, Math.max(0, 15 - photos.length));
    const selectedFiles = availableFiles.filter((file) => (
      file.size >= 10 * 1024 && file.size <= 5 * 1024 * 1024
    ));
    if (selectedFiles.length !== availableFiles.length) {
      onValidationError("Google Business Profile photos must be between 10 KB and 5 MB.");
    }
    setPhotos((current) => [
      ...current,
      ...selectedFiles.map((file) => ({
        id: `${file.name}-${file.lastModified}-${Math.random()}`,
        file,
        name: file.name,
        previewUrl: URL.createObjectURL(file),
        category: categories[0]?.value || "ADDITIONAL",
        postTo: defaultDestination,
        caption: "",
        callToActionType: "",
        callToActionUrl: "",
      })),
    ]);
  };

  const updatePhoto = (id, fields) => setPhotos((current) => current.map((photo) => photo.id === id ? { ...photo, ...fields } : photo));
  const removePhoto = (id) => {
    setPhotos((current) => current.filter((photo) => photo.id !== id));
    setRemoveId(null);
  };
  const generateCaption = async (photo) => {
    const caption = await onGenerateCaption(photo);
    if (caption) updatePhoto(photo.id, { caption });
  };

  return (
    <Flex direction="column" gap="4">
      <Flex justify="between" align="center" gap="3" wrap="wrap">
        <Button variant="ghost" onClick={onBack}><ArrowLeft size={17} /> Back to Posts</Button>
        <UploadSteps step={step} />
        <Badge color="violet" radius="full">{locationName}{photos.length ? ` · ${photos.length} photos` : ""}</Badge>
      </Flex>
      <Separator size="4" />

      {step === 1 && (
        <Flex direction="column" gap="4">
          {photos.length === 0 ? (
            <Card size="4" style={{ border: "1px dashed var(--gray-a7)" }}>
              <Flex direction="column" align="center" gap="3" py="8">
                <Upload size={36} color="var(--blue-9)" />
                <Text size="4" weight="bold">Upload photos for Google</Text>
                <Text size="2" color="gray">Choose up to 15 JPG or PNG images, no larger than 5 MB each. Nothing will be posted yet.</Text>
                <Button onClick={() => fileInputRef.current?.click()}>Choose Photos</Button>
              </Flex>
            </Card>
          ) : (
            <Grid columns={{ initial: "1", sm: "2", lg: "3" }} gap="4">
              {photos.map((photo) => (
                <Card key={photo.id} size="2">
                  <Flex direction="column" gap="2">
                    <Flex justify="end"><IconButton size="1" color="gray" variant="solid" onClick={() => removePhoto(photo.id)}><X size={14} /></IconButton></Flex>
                    <PhotoPlaceholder photo={photo} height={210} />
                    <Text size="2" color="gray" truncate>{photo.name}</Text>
                  </Flex>
                </Card>
              ))}
            </Grid>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg"
            multiple
            hidden
            onChange={(event) => {
              addFiles(event.target.files);
              event.target.value = "";
            }}
          />
          <Flex justify="between" align="center" gap="3">
            <Button variant="ghost" disabled={photos.length >= 15} onClick={() => fileInputRef.current?.click()}>
              <Plus size={16} /> Add more photos
            </Button>
            <Flex align="center" gap="3">
              <Text size="2" color="gray">{photos.length} / 15 photos added</Text>
              <Button disabled={photos.length === 0} onClick={() => setStep(2)}>Next: Set Details</Button>
            </Flex>
          </Flex>
        </Flex>
      )}

      {step === 2 && (
        <Flex direction="column" gap="4">
          {photos.map((photo) => (
            <Card key={photo.id} size="3" style={{ ...panelStyle, overflow: "hidden" }}>
              <Grid columns={{ initial: "1", md: "0.8fr 1.2fr" }} gap="4">
                <div style={{ position: "relative" }}>
                  <PhotoPlaceholder photo={photo} height={330} />
                  {removeId === photo.id ? (
                    <Flex direction="column" align="center" justify="center" gap="3" style={{ position: "absolute", inset: 0, background: "var(--red-a8)", borderRadius: "var(--radius-3)" }}>
                      <Text
                        weight="bold"
                        style={{ color: "white", textShadow: "0 1px 3px rgba(0, 0, 0, 0.45)" }}
                      >
                        Remove this photo?
                      </Text>
                      <Flex gap="2">
                        <Button color="red" variant="surface" onClick={() => setRemoveId(null)}>Keep</Button>
                        <Button color="red" onClick={() => removePhoto(photo.id)}>Remove</Button>
                      </Flex>
                    </Flex>
                  ) : (
                    <IconButton color="gray" variant="solid" style={{ position: "absolute", right: 10, top: 10 }} onClick={() => setRemoveId(photo.id)}>
                      <X size={15} />
                    </IconButton>
                  )}
                </div>
                <Flex direction="column" gap="3">
                  <Text weight="bold">{photo.name}</Text>
                  <Flex direction="column" gap="1">
                    <Text size="1" weight="bold" color="gray">CATEGORY</Text>
                    <Select.Root value={photo.category} onValueChange={(value) => updatePhoto(photo.id, { category: value })}>
                      <Select.Trigger />
                      <Select.Content>{categories.map((category) => <Select.Item key={category.value} value={category.value}>{category.label}</Select.Item>)}</Select.Content>
                    </Select.Root>
                  </Flex>
                  <Flex direction="column" gap="1">
                    <Text size="1" weight="bold" color="gray">POST TO</Text>
                    <Flex gap="2">
                      <Button variant={photo.postTo === "photo" ? "solid" : "outline"} onClick={() => updatePhoto(photo.id, { postTo: "photo", callToActionType: "", callToActionUrl: "" })}>Google Photo</Button>
                      <Button variant={photo.postTo === "post" ? "solid" : "outline"} onClick={() => updatePhoto(photo.id, { postTo: "post" })}>Google Post</Button>
                    </Flex>
                  </Flex>
                  <Flex direction="column" gap="1">
                    <Flex justify="between" align="center">
                      <Text size="1" weight="bold" color="gray">CAPTION</Text>
                      <Button
                        size="1"
                        variant="outline"
                        disabled={Boolean(generatingPhotoId)}
                        onClick={() => generateCaption(photo)}
                      >
                        <Sparkles size={13} /> {generatingPhotoId === photo.id ? "Generating..." : "Generate with AI"}
                      </Button>
                    </Flex>
                    <TextArea rows={5} maxLength={300} placeholder="Write a caption for this photo..." value={photo.caption} onChange={(event) => updatePhoto(photo.id, { caption: event.target.value })} />
                    <Text size="1" color="gray" align="right">{photo.caption.length} / 300 characters</Text>
                  </Flex>
                  {photo.postTo === "post" && (
                    <GooglePostButtonFields
                      draft={photo}
                      setDraft={(updater) => {
                        const nextPhoto = typeof updater === "function" ? updater(photo) : updater;
                        updatePhoto(photo.id, nextPhoto);
                      }}
                    />
                  )}
                </Flex>
              </Grid>
            </Card>
          ))}
          <Flex justify="between" align="center" gap="3">
            <Button color="gray" variant="soft" onClick={() => setStep(1)}>Back</Button>
            <Flex direction="column" align="end" gap="1">
              {hasInvalidPostButton && <Text size="1" color="red">Enter a valid link for each selected button.</Text>}
              <Button disabled={photos.length === 0 || hasInvalidPostButton} onClick={() => setStep(3)}>Next: Review</Button>
            </Flex>
          </Flex>
        </Flex>
      )}

      {step === 3 && (
        <Flex direction="column" gap="4">
          <Flex direction="column" gap="1"><Text size="5" weight="bold">Review your photos</Text><Text color="gray">Everything look good? Add them to your queue.</Text></Flex>
          {photos.map((photo) => (
            <Card key={photo.id} size="2">
              <Flex align="center" gap="3">
                <div style={{ width: 64, flexShrink: 0 }}><PhotoPlaceholder photo={photo} height={64} /></div>
                <Flex direction="column" gap="1" style={{ flex: 1, minWidth: 0 }}><Text weight="bold">{photo.name}</Text><Text size="2" color="gray" truncate>{photo.caption || "No caption added"}</Text></Flex>
                <Badge color={categoryColor(photo.category)} variant="soft">{categoryLabel(photo.category)}</Badge>
                <Badge color={photo.postTo === "post" ? "green" : "violet"} variant="soft">Google {photo.postTo === "post" ? "Post" : "Photo"}</Badge>
                <Button variant="ghost" onClick={() => setStep(2)}>Edit</Button>
              </Flex>
            </Card>
          ))}
          <Card size="3" style={{ background: "var(--blue-a2)", border: "1px solid var(--blue-a5)" }}>
            <Flex direction="column" gap="2">
              <Text weight="bold">These photos will be added to your queue</Text>
              <Text size="2" color="gray">
                {schedule.enabled
                  ? `Posting schedule: ${scheduleCadence(schedule)} · ${locationName}`
                  : `Auto-scheduling is off · ${locationName}`}
              </Text>
              {photos.map((photo, index) => <Text key={photo.id} size="2"><CalendarDays size={14} style={{ display: "inline", marginRight: 8 }} />{photo.name} → queue position {index + 1}</Text>)}
            </Flex>
          </Card>
          <Flex justify="between"><Button color="gray" variant="soft" onClick={() => setStep(2)} disabled={isSaving}>Back</Button><Button disabled={isSaving} onClick={() => onFinish(photos)}>{isSaving ? "Adding..." : `Add ${photos.length} Photos to Queue`}</Button></Flex>
        </Flex>
      )}
    </Flex>
  );
}

export default function ReviewsPosts() {
  const { locationsData = [] } = useLocationContext();
  const { userProfile } = useProfileContext();
  const [searchParams] = useSearchParams();
  const requestedLocationId = searchParams.get("location");
  const { setToastMessage } = useToast();
  const [selectedLocationId, setSelectedLocationId] = useState(requestedLocationId || locationsData[0]?.id?.toString() || "preview");
  const [screen, setScreen] = useState("dashboard");
  const [photos, setPhotos] = useState([]);
  const [monthDate, setMonthDate] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [generatingPhotoId, setGeneratingPhotoId] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const selectedLocation = useMemo(() => locationsData.find((location) => location.id?.toString() === selectedLocationId) || locationsData[0], [locationsData, selectedLocationId]);
  const locationName = selectedLocation?.name || "Location";
  const organisationId = userProfile?.organisation_id;
  const locationId = selectedLocation?.id;
  const postsQuery = useGetGooglePosts(
    organisationId,
    locationId,
    Boolean(locationId && selectedLocationId !== "preview"),
  );
  const uploadMutation = useUploadGooglePosts(organisationId, locationId);
  const cancelMutation = useCancelGooglePost(organisationId, locationId);
  const scheduleMutation = useUpdateGooglePostSchedule(organisationId, locationId);
  const captionMutation = useGenerateGooglePostCaption(locationId);
  const queueItemMutation = useUpdateGooglePostQueueItem(organisationId, locationId);
  const responseData = postsQuery.data?.data;
  const categories = responseData?.categories?.length
    ? responseData.categories
    : DEFAULT_PHOTO_CATEGORIES;
  const queue = useMemo(
    () => (responseData?.queue || []).map(mapQueueItem),
    [responseData?.queue],
  );
  const published = useMemo(
    () => (responseData?.published || []).map(mapQueueItem),
    [responseData?.published],
  );
  const schedule = useMemo(() => {
    const value = responseData?.schedule;
    return {
      enabled: value?.enabled ?? true,
      interval: String(value?.interval_count || 1),
      frequency: value?.frequency === "day"
        ? "Day"
        : value?.frequency === "month" ? "Month" : "Week",
      day: WEEK_DAYS[value?.day_of_week ?? 0],
      time: formatTimeValue(value?.posting_time || "09:00"),
      postTo: value?.default_destination || "photo",
    };
  }, [responseData?.schedule]);

  useEffect(() => {
    const selectionExists = locationsData.some(
      (location) => location.id?.toString() === selectedLocationId,
    );
    if (!selectionExists && locationsData[0]?.id) {
      const requestedLocationExists = locationsData.some(
        (location) => location.id?.toString() === requestedLocationId,
      );
      setSelectedLocationId(requestedLocationExists ? requestedLocationId : locationsData[0].id.toString());
    }
  }, [locationsData, requestedLocationId, selectedLocationId]);

  useEffect(() => {
    setPhotos((current) => {
      current.forEach((photo) => URL.revokeObjectURL(photo.previewUrl));
      return [];
    });
    setScreen("dashboard");
  }, [selectedLocationId]);

  const errorMessage = (error, fallback) => (
    error?.response?.data?.message
    || Object.values(error?.response?.data?.errors || {}).flat().join(" ")
    || fallback
  );

  const addPhotosToQueue = async (newPhotos) => {
    const formData = new FormData();
    formData.append("organisation_id", organisationId);
    newPhotos.forEach((photo) => formData.append("files", photo.file));
    formData.append("items", JSON.stringify(newPhotos.map((photo, index) => ({
      file_index: index,
      caption: photo.caption,
      category: photo.category,
      destination: photo.postTo,
      call_to_action_type: photo.callToActionType || "",
      call_to_action_url: photo.callToActionUrl || "",
    }))));

    try {
      await uploadMutation.mutateAsync(formData);
      newPhotos.forEach((photo) => URL.revokeObjectURL(photo.previewUrl));
      setPhotos([]);
      setScreen("dashboard");
      setToastMessage(`${newPhotos.length} photo${newPhotos.length === 1 ? "" : "s"} added to the queue`);
    } catch (error) {
      setToastMessage(errorMessage(error, "Unable to add the photos to the queue"));
    }
  };

  const cancelQueueItem = async (item) => {
    try {
      await cancelMutation.mutateAsync(item.id);
      setToastMessage(`${item.title} removed from the queue`);
      return true;
    } catch (error) {
      setToastMessage(errorMessage(error, "Unable to remove the queued photo"));
      return false;
    }
  };

  const postQueueItemLater = async (item) => {
    try {
      await queueItemMutation.mutateAsync({
        queueItemId: item.id,
        data: {
          organisation_id: organisationId,
          action: "post_later",
        },
      });
      setToastMessage(`${item.title} moved to the next available slot`);
    } catch (error) {
      setToastMessage(errorMessage(error, "Unable to postpone the queued photo"));
    }
  };

  const postQueueItemNow = async (item) => {
    try {
      await queueItemMutation.mutateAsync({
        queueItemId: item.id,
        data: {
          organisation_id: organisationId,
          action: "post_now",
        },
      });
      setToastMessage(`${item.title} sent for publishing`);
    } catch (error) {
      setToastMessage(errorMessage(error, "Unable to publish the queued photo"));
    }
  };

  const saveQueueItem = async (item, values) => {
    try {
      await queueItemMutation.mutateAsync({
        queueItemId: item.id,
        data: {
          organisation_id: organisationId,
          action: "edit",
          caption: values.caption,
          category: values.category,
          destination: values.destination,
          call_to_action_type: values.callToActionType || "",
          call_to_action_url: values.callToActionUrl || "",
        },
      });
      setEditingItem(null);
      setToastMessage(`${item.title} updated`);
    } catch (error) {
      setToastMessage(errorMessage(error, "Unable to update the queued photo"));
    }
  };

  const saveSchedule = async (nextSettings) => {
    try {
      await scheduleMutation.mutateAsync({
        organisation_id: organisationId,
        enabled: nextSettings.enabled,
        interval_count: Number(nextSettings.interval),
        frequency: nextSettings.frequency.toLowerCase(),
        day_of_week: WEEK_DAYS.indexOf(nextSettings.day),
        posting_time: parseTimeValue(nextSettings.time),
        default_destination: nextSettings.postTo,
      });
      setScheduleOpen(false);
      setToastMessage("Schedule settings saved");
    } catch (error) {
      setToastMessage(errorMessage(error, "Unable to save the schedule settings"));
    }
  };

  const generateCaption = async (photo) => {
    setGeneratingPhotoId(photo.id);
    try {
      const response = await captionMutation.mutateAsync({
        organisation_id: organisationId,
        filename: photo.name,
        category: photo.category,
        destination: photo.postTo,
      });
      return response.data.caption;
    } catch (error) {
      setToastMessage(errorMessage(error, "Unable to generate a caption"));
      return null;
    } finally {
      setGeneratingPhotoId(null);
    }
  };

  return (
    <Flex direction="column" gap="4" style={{ width: "100%", height: "100%", minWidth: 0, overflowY: "auto", paddingRight: 10 }}>
      <ToastAlert />
      {screen === "dashboard" && (
        <PostsDashboard
          locationName={locationName}
          locationControl={locationsData.length > 0 ? (
            <Select.Root value={selectedLocationId} onValueChange={setSelectedLocationId}>
              <Select.Trigger style={{ width: 220 }}>
                <Flex align="center" gap="2" style={{ minWidth: 0 }}>
                  <MapPin size={15} style={{ flexShrink: 0 }} />
                  <Text truncate>{locationName}</Text>
                </Flex>
              </Select.Trigger>
              <Select.Content>{locationsData.map((location) => <Select.Item key={location.id} value={location.id.toString()}>{location.name}</Select.Item>)}</Select.Content>
            </Select.Root>
          ) : null}
          queue={queue}
          published={published}
          monthDate={monthDate}
          setMonthDate={setMonthDate}
          schedule={schedule}
          categories={categories}
          editingItem={editingItem}
          isReady={Boolean(locationId && !postsQuery.isError)}
          onUpload={() => setScreen("upload")}
          onSchedule={() => setScheduleOpen(true)}
          onEdit={setEditingItem}
          onCancelEdit={() => setEditingItem(null)}
          onSaveEdit={saveQueueItem}
          onPostLater={postQueueItemLater}
          onPostNow={postQueueItemNow}
          onCancel={cancelQueueItem}
          isLoading={postsQuery.isLoading}
          isUpdating={queueItemMutation.isPending || cancelMutation.isPending}
        />
      )}
      {postsQuery.isError && screen === "dashboard" && (
        <Card size="3"><Text color="red">{errorMessage(postsQuery.error, "Unable to load the Google photo queue")}</Text></Card>
      )}
      {screen === "upload" && (
        <UploadScreen
          locationName={locationName}
          photos={photos}
          setPhotos={setPhotos}
          categories={categories}
          schedule={schedule}
          defaultDestination={schedule.postTo}
          onBack={() => setScreen("dashboard")}
          onFinish={addPhotosToQueue}
          onGenerateCaption={generateCaption}
          onValidationError={setToastMessage}
          generatingPhotoId={generatingPhotoId}
          isSaving={uploadMutation.isPending}
        />
      )}
      <ScheduleDialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        locationName={locationName}
        settings={schedule}
        onSave={saveSchedule}
        isSaving={scheduleMutation.isPending}
      />
    </Flex>
  );
}
